import type { KeyboardEvent as ReactKeyboardEvent, SyntheticEvent } from 'react'
import { useCallback, useRef, useState } from 'react'
import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import toLower from 'lodash/toLower'
import type { SelectProps } from '@ztechapps/webui.input'

/**
 * Shared adapter logic for `SpreadsheetCellSelect` and `SpreadsheetCellSelectCreatable`. Both
 * components bridge react-select to the spreadsheet keyboard model with identical wiring; only the
 * underlying component (`<Select>` vs `<SelectCreatable>`) differs.
 *
 * - Overrides `autoFocus={true}` so the dropdown's internal `<input>` claims focus when the cell
 *   enters edit mode. Without this, react-select's `defaultMenuIsOpen` only opens the menu — the
 *   input never receives focus, arrow/letter keypresses are swallowed by the `<td>`, and the
 *   dropdown is keyboard-dead. Only one cell mounts a Select at a time (only the editing cell),
 *   so no cross-cell focus steal is possible.
 * - Forwards `onMenuOpen` / `onMenuClose` through `onMenuStateChange?(isOpen)` for any consumer that
 *   wants to gate behavior on the dropdown's open state.
 * - Wraps `onChange` to also fire an `onCommit` callback so the cell consumer can exit edit mode
 *   when the user picks an option.
 * - The wrapper `<div>` carries `mouseDown` / `click` stopPropagation so clicks on the menu portal
 *   do not bubble out and dismiss a non-static parent modal.
 * - Tracks the typed text and commits it when the cell is left by a click (blur), a typed-edit
 *   arrow or Shift+Tab: text matching an option label ignoring case commits that option, other
 *   text in a creatable cell commits as a new option, and anything else is discarded. Escape
 *   drops the typed text and default-prevents it, so react-select never reaches its own Escape
 *   branch — which would clear the value on a closed menu and crash the change handler reading
 *   the cleared value — while the container still reverts and ends the edit.
 * - A typed edit opens its menu on focus, so the current option is highlighted when the typed
 *   text matches it, else the first filtered option. An Enter-started edit keeps the mount-time
 *   highlight.
 * - The menu is controlled: seeded from `defaultMenuIsOpen`, synced on every open/close, and
 *   closed on blur after the commit attempt. An uncontrolled menu stays open on an outside press
 *   because the menu's own mousedown keeps focus in the input, so blur never fires on its own —
 *   the grid container blurs the editor instead (see `useSpreadsheetNavigation`), and this close
 *   is what dismisses the menu there.
 *
 * Keyboard handling is split: this adapter commits typed text on leave keys and blur and keeps
 * Escape from react-select; the grid container moves the selection and reverts.
 */
export type SpreadsheetCellSelectAdapterArgs = {
  onCommit?: () => void
  onMenuStateChange?: (isOpen: boolean) => void
  onChange?: (newValue: any, actionMeta: any) => void
  onMenuOpen?: () => void
  onMenuClose?: () => void
  defaultMenuIsOpen?: boolean
  defaultInputValue?: string
  options?: SelectProps['options']
  isCreatable?: boolean
  onInputChange?: SelectProps['onInputChange']
}

export type SpreadsheetCellSelectAdapterResult = {
  /** Spread on the wrapper `<div>` surrounding the underlying Select. */
  wrapperProps: {
    onMouseDown: (e: SyntheticEvent) => void
    onClick: (e: SyntheticEvent) => void
  }
  /** Spread on the underlying `<Select>` / `<SelectCreatable>` element. */
  selectProps: {
    autoFocus: true
    openMenuOnFocus: boolean
    menuIsOpen: boolean
    onChange: (newValue: any, actionMeta: any) => void
    onInputChange: SelectProps['onInputChange']
    onKeyDown: SelectProps['onKeyDown']
    onBlur: SelectProps['onBlur']
    onMenuOpen: () => void
    onMenuClose: () => void
  }
}

export const useSpreadsheetCellSelectAdapter = ({
  onCommit,
  onMenuStateChange,
  onChange,
  onMenuOpen,
  onMenuClose,
  defaultMenuIsOpen,
  defaultInputValue,
  options,
  isCreatable,
  onInputChange,
}: SpreadsheetCellSelectAdapterArgs): SpreadsheetCellSelectAdapterResult => {
  // Mirrors read by the leave-key and blur handlers so a commit always sees the latest options
  // and callbacks even when react-select holds an earlier handler reference.
  const optionsRef = useRef(options)
  optionsRef.current = options
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const onCommitRef = useRef(onCommit)
  onCommitRef.current = onCommit
  const onInputChangeRef = useRef(onInputChange)
  onInputChangeRef.current = onInputChange

  // Holds the seed character plus anything typed since. Any non-typing input action — a pick,
  // a menu close, a blur clear — resets it, so a later blur commits nothing twice.
  const typedTextRef = useRef(defaultInputValue ?? '')

  // Controlled open state, seeded from the mount-time default. Every react-select open/close
  // funnels through the handlers below, so this mirrors the menu and can shut it on blur.
  const [menuIsOpen, setMenuIsOpen] = useState(defaultMenuIsOpen ?? false)

  const handleMenuOpen = useCallback(() => {
    setMenuIsOpen(true)
    onMenuStateChange?.(true)
    onMenuOpen?.()
  }, [onMenuStateChange, onMenuOpen])

  const handleMenuClose = useCallback(() => {
    setMenuIsOpen(false)
    onMenuStateChange?.(false)
    onMenuClose?.()
  }, [onMenuStateChange, onMenuClose])

  const handleChange = useCallback((newValue: any, actionMeta: any) => {
    onChangeRef.current?.(newValue, actionMeta)
    onCommitRef.current?.()
  }, [])

  // Commits the tracked text as an option pick: a label match ignoring case wins, other text in
  // a creatable cell becomes a new option, and anything else is discarded. Always clears first
  // so a second leave (blur after an arrow) commits nothing.
  const commitTypedText = useCallback(() => {
    const text = typedTextRef.current
    typedTextRef.current = ''
    if (isEmpty(text)) {
      return
    }
    const match = find(optionsRef.current, (option) => toLower(option?.label) === toLower(text))
    if (match) {
      handleChange(match, { action: 'select-option', option: match })
      return
    }
    if (isCreatable) {
      const created = { label: text, value: text, __isNew__: true }
      handleChange(created, { action: 'create-option', option: created })
    }
  }, [handleChange, isCreatable])

  const handleInputChange: SelectProps['onInputChange'] = useCallback((value, actionMeta) => {
    typedTextRef.current = actionMeta?.action === 'input-change' ? value : ''
    return onInputChangeRef.current?.(value, actionMeta)
  }, [])

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      if (event.key === 'Escape') {
        // react-select skips its own Escape branch on a prevented event, so a closed menu never
        // clears the value into the change handler. The container still reverts the edit.
        typedTextRef.current = ''
        event.preventDefault()
        return
      }
      // Typed-edit leave keys commit here; the container then moves. Never prevented, so caret
      // keys in an Enter-started edit stay inside the editor.
      if (
        ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && !isEmpty(defaultInputValue)) ||
        (event.key === 'Tab' && event.shiftKey)
      ) {
        commitTypedText()
      }
    },
    [commitTypedText, defaultInputValue]
  )

  // react-select runs the caller's blur before clearing the input, so the tracked text is still
  // intact when this commits it. The menu closes after the commit attempt either way, so an
  // outside press the container turns into a blur dismisses the menu it could never blur itself.
  const handleBlur = useCallback(() => {
    commitTypedText()
    setMenuIsOpen(false)
  }, [commitTypedText])

  const stopProp = useCallback((e: SyntheticEvent) => e.stopPropagation(), [])

  return {
    wrapperProps: {
      onMouseDown: stopProp,
      onClick: stopProp,
    },
    selectProps: {
      autoFocus: true,
      openMenuOnFocus: !isEmpty(defaultInputValue),
      menuIsOpen,
      onChange: handleChange,
      onInputChange: handleInputChange,
      onKeyDown: handleKeyDown,
      onBlur: handleBlur,
      onMenuOpen: handleMenuOpen,
      onMenuClose: handleMenuClose,
    },
  }
}
